import { makeAutoObservable, runInAction } from 'mobx';

import messageBoxStore from 'MessageBox/MessageBoxStore';
import popupStore from 'components/Popup/PopupStore';

import client, { ClientError } from 'client';

/** Тип модалки выпуска — Popup ищет себя по нему в popupStore. */
export const API_KEY_MODAL = 'apikeymodal';

/** Столько же, сколько в схеме БД: `name VARCHAR(64)`. */
export const MAX_NAME_LENGTH = 64;

/**
 * Ключи публичного API.
 *
 * Ключ целиком существует ровно один раз — в ответе на выпуск. Он живёт в
 * `issuedKey` до закрытия модалки и никуда больше не уезжает: сервер хранит
 * только хеш и второй раз ключ не покажет.
 */
class ApiKeysStore {
  /** @type {import('client').ApiKey[]} */
  items = [];

  /** @type {?number} Сколько действующих ключей разрешено; приходит с сервера. */
  limit = null;

  loading = false;
  loaded = false;

  /** Имя в поле модалки. */
  name = '';
  creating = false;

  /** @type {?string} Ключ целиком, показывается один раз. */
  issuedKey = null;
  copied = false;

  /** @type {?number} id ключа, который сейчас отзывается. */
  revokingId = null;

  constructor() {
    makeAutoObservable(this);
  }

  get activeCount() {
    return this.items.filter((item) => !item.revokedAt).length;
  }

  get canCreate() {
    return this.limit === null || this.activeCount < this.limit;
  }

  async fetch() {
    if (this.loading) return;
    this.loading = true;
    try {
      const result = await client.getApiKeys();
      runInAction(() => {
        this.items = result.items;
        this.limit = result.limit;
        this.loaded = true;
      });
    } catch (e) {
      if (!(e instanceof ClientError)) throw e;
      messageBoxStore.showError(e.message);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  openCreateModal() {
    this.name = '';
    this.issuedKey = null;
    this.copied = false;
    popupStore.open(API_KEY_MODAL);
  }

  /**
   * Закрытие модалки — единственный момент, когда ключ пропадает совсем.
   * Поэтому же его чистим здесь, а не в `openCreateModal`: пока модалка
   * открыта, пользователь ещё может его скопировать.
   */
  closeCreateModal() {
    popupStore.close();
    this.issuedKey = null;
    this.copied = false;
    this.name = '';
  }

  setName(value) {
    this.name = value.slice(0, MAX_NAME_LENGTH);
  }

  async create() {
    const name = this.name.trim();
    if (!name || this.creating) return;

    this.creating = true;
    try {
      const created = await client.createApiKey(name);
      runInAction(() => {
        this.issuedKey = created.key;
        this.copied = false;
        // В списке ключа целиком быть не должно даже в памяти вкладки.
        const { key, ...row } = created;
        this.items = [row, ...this.items];
      });
    } catch (e) {
      if (!(e instanceof ClientError)) throw e;
      messageBoxStore.showError(e.message);
    } finally {
      runInAction(() => {
        this.creating = false;
      });
    }
  }

  async copyIssuedKey() {
    if (!this.issuedKey) return;
    try {
      await navigator.clipboard.writeText(this.issuedKey);
      runInAction(() => {
        this.copied = true;
      });
    } catch (e) {
      // Буфер обмена недоступен по http и в части мобильных браузеров. Ключ
      // при этом на экране — его можно выделить руками, и об этом надо сказать,
      // а не молчать.
      messageBoxStore.showError(
        'Не удалось скопировать ключ. Выделите и скопируйте его вручную.'
      );
    }
  }

  /**
   * Отзыв через общее подтверждение: в тексте названо имя ключа — по нему
   * владелец и понимает, какую интеграцию сейчас сломает.
   *
   * @param {import('client').ApiKey} item
   */
  revokeWithConfirm(item) {
    popupStore.setText(
      `Отозвать ключ «${item.name}»? Интеграции, которые им пользуются, перестанут работать сразу.`
    );
    popupStore.setOk(() => this.revoke(item.id));
    popupStore.open('suremodal');
  }

  /** @param {number} id */
  async revoke(id) {
    if (this.revokingId !== null) return;
    this.revokingId = id;
    try {
      await client.revokeApiKey(id);
      await this.fetch();
    } catch (e) {
      if (!(e instanceof ClientError)) throw e;
      messageBoxStore.showError(e.message);
    } finally {
      runInAction(() => {
        this.revokingId = null;
      });
    }
  }
}

const apiKeysStore = new ApiKeysStore();
export default apiKeysStore;
